import React from 'react';

import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useMutationSetComponentSpecificFields } from 'api/mutations/setComponentSpecificFields.mutation';
import { useMutationSetDashboardById } from 'api/mutations/setDashboardById.mutation';
import { useQueryGetDashboards } from 'api/queries/getDashboards.query';

import { EditNarrative } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/Narrative/EditNarrative/editNarrative';
import 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/Narrative/EditNarrative/editNarrative.css';
import { ViewNarrative } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/Narrative/ViewNarrative/viewNarrative';
import { updateWidgetNarrativeSettings } from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import { IDashboard } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';

interface Props {
    isEdit: boolean;
    value: string;
    handleCancel: () => void;
    handleSave: () => void;
    widgetId?: string;
    dashboard?: IDashboard;
    reportId?: number;
}

export const Narrative = ({ isEdit, value, handleCancel, handleSave, widgetId, dashboard, reportId }: Props) => {
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const { refetch } = useQueryGetDashboards();
    const { mutate: mutateSetDashboardById } = useMutationSetDashboardById();
    const { mutate: mutateSetComponentSpecificFields } = useMutationSetComponentSpecificFields();

    const onSaveInReport = (description: string, isGenerativeAi: boolean, reportId: number) => {
        mutateSetComponentSpecificFields(
            {
                componentId: widgetId,
                fieldsToUpdate: {
                    description,
                    showNarrative: isGenerativeAi,
                },
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: ['getReport', { id: reportId }],
                    });
                    handleSnackBar('Successfully updated!', 'success');
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    handleSave();
                },
            },
        );
    };

    const onSaveInPinboard = (description: string, isGenerativeAi: boolean) => {
        if (!dashboard) return;

        const newDashboard = updateWidgetNarrativeSettings({
            dashboard,
            description,
            showGenerativeNarrative: isGenerativeAi,
            widgetId,
        });

        mutateSetDashboardById(
            {
                dashboard: newDashboard,
            },
            {
                onSuccess: () => handleSnackBar('Successfully updated!', 'success'),
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    refetch().then(() => handleSave());
                },
            },
        );
    };

    const onSave = (description: string, isGenerativeAi: boolean) => {
        if (reportId) {
            onSaveInReport(description, isGenerativeAi, reportId);
        } else {
            onSaveInPinboard(description, isGenerativeAi);
        }
    };

    return isEdit ? (
        <EditNarrative value={value} handleCancel={handleCancel} onSave={onSave} />
    ) : (
        <ViewNarrative value={value} />
    );
};
